import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  getCustomerSleeveData,
  verifyCustomerSleeveOtp,
  submitCustomerSleeveAnswers
} from "../../services/CommonService";
import Utils from "../../utils";
import { Lang } from "../../lang";
import { toast } from "react-toastify";
import { CUSTOMER_PORTAL } from "../../configs/AppConfig";
import CustomerSleeveLayout from './CustomerSleeveLayout';
import OTPVerifyCard from './OTPVerifyCard';
import './ClientSleeve.css';

const CustomerSleeve = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [envelopeData, setEnvelopeData] = useState({});
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState(true);
  const [isCompleted, setIsCompleted] = useState(false);
  const [errors, setErrors] = useState({ password: '' });
  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
  const [openFormIds, setOpenFormIds] = useState([]);
  const [openGroupIds, setOpenGroupIds] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [questionAnswers, setQuestionAnswers] = useState({});
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedPreviewGroupIndex, setSelectedPreviewGroupIndex] = useState(0);
  const [selectedPreviewFormIndex, setSelectedPreviewFormIndex] = useState(0);
  const [inputErrors, setInputErrors] = useState({}); // <-- new for input errors

  const openPasswordModal = useRef(null);
  const clsPasswordModal = useRef(null);

  // Fetch sleeve data on component mount
  useEffect(() => {
    document.title = Lang.client_portal_title;

    const fetchSleeveData = async () => {
      try {
        const response = await getCustomerSleeveData({ sleeve_uuid: id });
        setEnvelopeData(response.data.data);

        const localStore = sessionStorage.getItem(CUSTOMER_PORTAL);
        if (localStore && localStore === id) {
          setOtp(false);
        }
      } catch (err) {
        setError(Utils.getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };

    fetchSleeveData();
  }, [id]);

  // Check if sleeve is completed
  useEffect(() => {
    if (envelopeData?.sleeve_uuid) {
      const completed = localStorage.getItem(`sleeve_completed_${envelopeData.sleeve_uuid}`);
      setIsCompleted(!!completed);
    }
  }, [envelopeData?.sleeve_uuid]);

  // Set initial question when forms are loaded
  useEffect(() => {
    if (envelopeData?.forms?.length > 0) {
      const firstForm = envelopeData.forms[0];
      const firstGroup = firstForm.groups?.[0];
      const firstQuestion = firstGroup?.questions?.[0];

      if (firstQuestion) {
        setSelectedQuestion({
          formId: firstForm.form_id,
          groupId: firstGroup.group_id,
          questionId: firstQuestion.question_id,
        });
        setOpenFormIds([firstForm.form_id]);
        setOpenGroupIds([firstGroup.group_id]);
      }
    }
  }, [envelopeData]);

  // Reset preview indices when modal is shown/hidden
  useEffect(() => {
    if (showPreviewModal) {
      setSelectedPreviewFormIndex(0);
      setSelectedPreviewGroupIndex(0);
    }
  }, [showPreviewModal]);

  // Get all questions in a flat array
  const getAllQuestionsFlat = useCallback(() => {
    if (!envelopeData?.forms) return [];
    return envelopeData.forms.flatMap(form =>
      form.groups.flatMap(group =>
        group.questions.map(q => ({
          formId: form.form_id,
          groupId: group.group_id,
          questionId: q.question_id,
          form,
          group,
          q
        }))
      )
    );
  }, [envelopeData]);

  const allQuestionsFlat = getAllQuestionsFlat();
  const currentQIndex = selectedQuestion
    ? allQuestionsFlat.findIndex(
      x => x.formId === selectedQuestion.formId &&
        x.groupId === selectedQuestion.groupId &&
        x.questionId === selectedQuestion.questionId
    )
    : -1;

  // Navigation handlers
  const navigateQuestion = useCallback((direction) => {
    if (direction === 'prev' && currentQIndex > 0) {
      const prevQ = allQuestionsFlat[currentQIndex - 1];
      setSelectedQuestion({
        formId: prevQ.formId,
        groupId: prevQ.groupId,
        questionId: prevQ.questionId
      });
      setOpenFormIds(ids => ids.includes(prevQ.formId) ? ids : [...ids, prevQ.formId]);
      setOpenGroupIds(ids => ids.includes(prevQ.groupId) ? ids : [...ids, prevQ.groupId]);
    } else if (direction === 'next' && currentQIndex < allQuestionsFlat.length - 1) {
      const nextQ = allQuestionsFlat[currentQIndex + 1];
      setSelectedQuestion({
        formId: nextQ.formId,
        groupId: nextQ.groupId,
        questionId: nextQ.questionId
      });
      setOpenFormIds(ids => ids.includes(nextQ.formId) ? ids : [...ids, nextQ.formId]);
      setOpenGroupIds(ids => ids.includes(nextQ.groupId) ? ids : [...ids, nextQ.groupId]);
    }
  }, [allQuestionsFlat, currentQIndex]);

  // Accordion toggle handlers
  const toggleFormAccordion = (formId) => {
    setOpenFormIds(prev =>
      prev.includes(formId) ? prev.filter(id => id !== formId) : [...prev, formId]
    );
  };

  const toggleGroupAccordion = (groupId) => {
    setOpenGroupIds(prev =>
      prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]
    );
  };

  const handleQuestionSelect = (form, group, q) => {
    setSelectedQuestion({
      formId: form.form_id,
      groupId: group.group_id,
      questionId: q.question_id
    });
  };

  // Password form handler
  const handlePasswordForm = (e) => {
    e.preventDefault();
    let errorObj = { password: '' };
    let hasError = false;

    if (!password) {
      errorObj.password = 'Please enter password';
      hasError = true;
    } else if (password !== envelopeData.password) {
      errorObj.password = 'Entered password is wrong.';
      hasError = true;
    }

    setErrors(errorObj);
    if (hasError) return;

    clsPasswordModal?.current.click();
  };

  // OTP handlers
  const handleOtpChange = (e, idx) => {
    const val = e.target.value.replace(/[^0-9]/g, "");
    if (val.length > 1) return;
    const newOtp = [...otpValues];
    newOtp[idx] = val;
    setOtpValues(newOtp);
  };

  const handleOtpPaste = (e) => {
    const paste = e.clipboardData.getData('text').replace(/[^0-9]/g, "");
    if (paste.length === 6) {
      setOtpValues(paste.split(""));
    }
  };

  const handleVerify = async () => {
    setLoading(true);
    try {
      const otpValue = otpValues.join("");
      const payload = {
        sleeve_uuid: id,
        otp: otpValue
      };
      await verifyCustomerSleeveOtp(payload);
      setOtp(false);
      sessionStorage.setItem(CUSTOMER_PORTAL, id);
    } catch (err) {
      toast.error(Utils.getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Answer submission handler
  const handleSubmitAnswers = async () => {
    // Validate required questions
    for (const q of allQuestionsFlat) {
      if (!q.q.is_required) continue;

      const ans = questionAnswers[q.questionId];
      const isAnswered = q.q.question_type === "checkbox"
        ? Array.isArray(ans) && ans.length > 0
        : !!ans;

      if (!isAnswered) {
        toast.error("Please fill all required fields.");
        return;
      }
    }

    setLoading(true);
    try {
      const answersArray = allQuestionsFlat.map(q => {
        const ans = questionAnswers[q.questionId];

        if (["text", "number", "email", "textarea", "date"].includes(q.q.question_type)) {
          return {
            question_id: q.questionId,
            answer_text: ans || "",
            answer_option_id: null,
            answer_option_ids: []
          };
        } else if (q.q.question_type === "radio") {
          return {
            question_id: q.questionId,
            answer_text: null,
            answer_option_id: ans || null,
            answer_option_ids: []
          };
        } else if (q.q.question_type === "checkbox") {
          return {
            question_id: q.questionId,
            answer_text: null,
            answer_option_id: null,
            answer_option_ids: Array.isArray(ans) ? ans : []
          };
        }
        return {
          question_id: q.questionId,
          answer_text: ans || "",
          answer_option_id: null,
          answer_option_ids: []
        };
      });

      const payload = {
        sleeve_uuid: envelopeData.sleeve_uuid,
        recipient_id: envelopeData.recipient_id,
        answers: answersArray
      };

      await submitCustomerSleeveAnswers(payload);
      localStorage.setItem(`sleeve_completed_${envelopeData.sleeve_uuid}`, "1");
      setIsCompleted(true);
      toast.success("Answers submitted successfully!");
      setShowPreviewModal(false);
    } catch (err) {
      toast.error("Failed to submit answers. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Email validation function
  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  // Render question input based on type
  const renderQuestionInput = (q) => {
    if (!q) return null;
    const value = questionAnswers[q.question_id] || '';

    const commonProps = {
      className: `cs-input-${q.question_type}`,
      value: value,
      onChange: e => setQuestionAnswers(prev => ({
        ...prev,
        [q.question_id]: e.target.value
      })),
      placeholder: `Enter ${q.question_text}`
    };

    switch (q.question_type) {
      case 'text':
      case 'number':
        return <input type={q.question_type} {...commonProps} />;
      case 'email':
        return <>
          <input
            type="email"
            {...commonProps}
            onBlur={e => {
              const val = e.target.value;
              if (val && !isValidEmail(val)) {
                setInputErrors(prev => ({ ...prev, [q.question_id]: 'Invalid email address' }));
              } else {
                setInputErrors(prev => {
                  const copy = { ...prev };
                  delete copy[q.question_id];
                  return copy;
                });
              }
            }}
          />
          {inputErrors[q.question_id] && (
            <div className="text-danger small mt-1 ms-1">{inputErrors[q.question_id]}</div>
          )}
        </>;
      case 'date':
        let inputRef = null;
        return (
          <div className="date-input-container position-relative">
            <input
              type="date"
              className="cs-input-date pe-5"
              value={value}
              ref={el => inputRef = el}
              onChange={e => setQuestionAnswers(prev => ({
                ...prev,
                [q.question_id]: e.target.value
              }))}
            />
            <span
              className="calendar-icon"
              style={{
                position: 'absolute',
                right: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: 'pointer',
                color: '#888'
              }}
              onClick={() => {
                if (inputRef) {
                  inputRef.focus();
                  // Try showPicker if available (modern browsers)
                  if (typeof inputRef.showPicker === 'function') {
                    try { inputRef.showPicker(); } catch (e) { }
                  } else {
                    // Fallback: try click (may work in some browsers)
                    inputRef.click();
                  }
                }
              }}
            >
              <i className="bi bi-calendar3" />
            </span>
          </div>
        );
      case 'textarea':
        return <textarea className="cs-input-textarea" {...commonProps} />;
      case 'radio':
        return (
          <div className="cs-input-radio-group">
            {q.options.map(opt => (
              <div key={opt.option_id} className="form-check mb-1">
                <input
                  className="form-check-input cs-input-radio"
                  type="radio"
                  name={`radio_${q.question_id}`}
                  id={`radio_${q.question_id}_${opt.option_id}`}
                  value={opt.option_id}
                  checked={value === opt.option_id}
                  onChange={() => setQuestionAnswers(prev => ({
                    ...prev,
                    [q.question_id]: opt.option_id
                  }))}
                />
                <label className="form-check-label" htmlFor={`radio_${q.question_id}_${opt.option_id}`}>
                  {opt.name}
                </label>
              </div>
            ))}
          </div>
        );
      case 'checkbox':
        return (
          <div className="cs-input-checkbox-group">
            {q.options.map(opt => {
              const arr = Array.isArray(value) ? value : [];
              return (
                <div key={opt.option_id} className="form-check mb-1">
                  <input
                    className="form-check-input cs-input-checkbox"
                    type="checkbox"
                    id={`checkbox_${q.question_id}_${opt.option_id}`}
                    value={opt.option_id}
                    checked={arr.includes(opt.option_id)}
                    onChange={e => {
                      setQuestionAnswers(prev => {
                        const prevArr = Array.isArray(prev[q.question_id]) ? prev[q.question_id] : [];
                        return {
                          ...prev,
                          [q.question_id]: e.target.checked
                            ? [...prevArr, opt.option_id]
                            : prevArr.filter(id => id !== opt.option_id)
                        };
                      });
                    }}
                  />
                  <label className="form-check-label" htmlFor={`checkbox_${q.question_id}_${opt.option_id}`}>
                    {opt.name}
                  </label>
                </div>
              );
            })}
          </div>
        );
      default:
        return <input type="text" className="form-control" disabled placeholder="Unsupported question type" />;
    }
  };

  // Get question icon based on type
  const getQuestionIcon = (type) => {
    const icons = {
      text: 'bi-type',
      email: 'bi-envelope',
      number: 'bi-hash',
      date: 'bi-calendar3',
      textarea: 'bi-textarea-resize',
      radio: 'bi-record-circle',
      checkbox: 'bi-check2-square',
      mobile: 'bi-telephone'
    };
    return icons[type] || 'bi-dot';
  };

  // Render sidebar question item
  const renderSidebarQuestionItem = (form, group, q) => {
    const ans = questionAnswers[q.question_id];
    const isAnswered = q.question_type === 'checkbox'
      ? Array.isArray(ans) && ans.length > 0
      : !!ans;

    return (
      <li
        key={q.question_id}
        className={`sidebar-question-item${selectedQuestion?.questionId === q.question_id ? ' selected' : ''}`}
        onClick={e => { e.stopPropagation(); handleQuestionSelect(form, group, q); }}
      >
        <i className={`bi ${getQuestionIcon(q.question_type)} me-2`} />
        <span style={{ fontSize: 15 }}>
          {q.question_text}
          {q.is_required && <span className="text-danger ms-1" title="Required">*</span>}
        </span>
        {isAnswered && (
          <i
            className="bi bi-check-circle-fill text-success ms-2"
            title="Answered"
            style={{ fontSize: 16, verticalAlign: 'middle' }}
          />
        )}
      </li>
    );
  };

  // Render preview answer content
  const renderPreviewAnswer = (q) => {
    const ans = questionAnswers[q.question_id];

    if (q.question_type === 'checkbox') {
      if (!Array.isArray(ans)) return <span className="preview-no-answer">No answer</span>;

      const selectedOpts = q.options?.filter(opt => ans.includes(opt.option_id)) || [];
      return selectedOpts.length > 0 ? (
        selectedOpts.map(opt => (
          <span key={opt.option_id} className="preview-answer-badge">
            {opt.name}
          </span>
        ))
      ) : (
        <span className="preview-no-answer">No answer</span>
      );
    }

    if (q.question_type === 'radio') {
      const opt = q.options?.find(opt => opt.option_id === ans);
      return opt ? (
        <span className="preview-answer-badge primary">
          {opt.name}
        </span>
      ) : (
        <span className="preview-no-answer">No answer</span>
      );
    }

    return ans ? (
      <span className="preview-text-answer">{ans}</span>
    ) : (
      <span className="preview-no-answer">No answer</span>
    );
  };

  return (
    <>
      {loading && (
        <div className="page-loading">
          <img src="/images/loader.gif" alt="loader" />
        </div>
      )}

      <section
        className="section-bg-color detail_page setting_tab mt-0 pt-0"
        style={{ minHeight: 'calc(100vh - 135.5px)' }}
      >
        <CustomerSleeveLayout envelopeData={envelopeData} minHeight="100vh">
          {/* OTP Verification */}
          {otp && (
            <OTPVerifyCard
              otpValues={otpValues}
              onOtpChange={handleOtpChange}
              onOtpPaste={handleOtpPaste}
              onVerify={handleVerify}
              loading={loading}
            />
          )}

          {/* Main Content */}
          {!otp && (
            <>
              {!error && (
                <>
                  {!(envelopeData.is_finish || isCompleted) ? (
                    <div className="section-bg-color">
                      <section
                        className="client_portal custom_container_portal d-flex"
                        style={{ minHeight: "calc(100vh - 135.5px)" }}
                      >
                        {/* Sidebar */}
                        <div className="customer-sleeve-sidebar">
                          <div className="customer-sleeve-sidebar-title fw-bold fs-5 mb-4">
                            {envelopeData?.sleeve_name || 'Forms'}
                          </div>

                          {envelopeData?.forms?.map((form) => (
                            <div key={form.form_id} className={`sidebar-form-title${openFormIds.includes(form.form_id) ? ' open' : ''}`}>
                              <div
                                className="d-flex align-items-center justify-content-between w-100"
                                onClick={() => toggleFormAccordion(form.form_id)}
                              >
                                <span>
                                  <i
                                    className={`bi bi-folder2${openFormIds.includes(form.form_id) ? '-open' : ''} me-2`}
                                    style={{ fontSize: 16, verticalAlign: 'middle' }}
                                  />
                                  {form.form_name}
                                </span>
                                <i
                                  className={`bi bi-chevron-${openFormIds.includes(form.form_id) ? 'down' : 'right'}`}
                                  style={{ fontSize: 15 }}
                                />
                              </div>

                              {openFormIds.includes(form.form_id) && (
                                <div className="sidebar-form-content ms-2 mt-2">
                                  {form.groups.map((group) => (
                                    <div className="mb-1" key={group.group_id}>
                                      <div
                                        className={`sidebar-group-title${openGroupIds.includes(group.group_id) ? ' open' : ''}`}
                                        onClick={e => { e.stopPropagation(); toggleGroupAccordion(group.group_id); }}
                                      >
                                        <div>
                                          <i
                                            className={`me-2 bi bi-chevron-${openGroupIds.includes(group.group_id) ? 'down' : 'right'}`}
                                            style={{ fontSize: 13 }}
                                          />
                                          <span>
                                            <i
                                              className={`bi bi-collection${openGroupIds.includes(group.group_id) ? '-fill' : ''} me-2`}
                                              style={{ fontSize: 15, verticalAlign: 'middle' }}
                                            />
                                            {group.group_name}
                                          </span>
                                        </div>
                                        <div>
                                          <span className="group-question-count ms-auto text-secondary small fw-semibold">
                                            {(() => {
                                              const total = group.questions.length;
                                              const attempted = group.questions.filter(q => {
                                                const ans = questionAnswers[q.question_id];
                                                if (q.question_type === 'checkbox') return Array.isArray(ans) && ans.length > 0;
                                                return !!ans;
                                              }).length;
                                              return `(${attempted}/${total})`;
                                            })()}
                                          </span>
                                        </div>
                                      </div>

                                      {openGroupIds.includes(group.group_id) && (
                                        <ul className="list-unstyled ms-3 mt-1 mb-2">
                                          {group.questions.map((q) => renderSidebarQuestionItem(form, group, q))}
                                        </ul>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>

                        {/* Main Panel */}
                        <div className="customer-sleeve-mainpanel flex-grow-1">
                          <div className="customer-sleeve-tab-content p-0 rounded-2 d-flex justify-content-center">
                            <div className="w-100 d-flex align-items-center justify-content-center py-3">
                              <div className="customer-sleeve-questionpanel position-relative">
                                {selectedQuestion ? (
                                  (() => {
                                    const form = envelopeData.forms?.find(f => f.form_id === selectedQuestion.formId);
                                    const group = form?.groups?.find(g => g.group_id === selectedQuestion.groupId);
                                    const q = group?.questions?.find(qq => qq.question_id === selectedQuestion.questionId);

                                    if (!q) return <div className="text-danger">Question not found.</div>;

                                    return (
                                      <>
                                        <div className="customer-sleeve-questionpanel-title mb-2 fw-semibold position-relative d-flex align-items-center justify-content-between">
                                          <span>
                                            {q.question_text}
                                            {q.is_required && (
                                              <span className="text-danger ms-1" title="Required">*</span>
                                            )}
                                          </span>
                                          {q.is_required && (
                                            <span className="badge bg-danger position-absolute" style={{ right: 0, top: 0, fontSize: '0.8rem' }}>
                                              Required
                                            </span>
                                          )}
                                        </div>
                                        {renderQuestionInput(q)}
                                        <div style={{ height: 60 }}></div>
                                        <div className="cs-question-nav-btns">
                                          <button
                                            className="btn btn-outline-primary sidebar-nav-btn"
                                            onClick={() => navigateQuestion('prev')}
                                            disabled={currentQIndex <= 0}
                                          >
                                            <i className="bi bi-arrow-left me-2"></i>Prev
                                          </button>
                                          {currentQIndex === allQuestionsFlat.length - 1 ? (
                                            <button
                                              className="btn btn-primary sidebar-nav-btn"
                                              type="button"
                                              onClick={() => setShowPreviewModal(true)}
                                            >
                                              Preview
                                            </button>
                                          ) : (
                                            <button
                                              className="btn btn-outline-primary sidebar-nav-btn"
                                              onClick={() => navigateQuestion('next')}
                                              disabled={currentQIndex === allQuestionsFlat.length - 1}
                                            >
                                              Next<i className="bi bi-arrow-right ms-2"></i>
                                            </button>
                                          )}
                                        </div>
                                      </>
                                    );
                                  })()
                                ) : (
                                  <div className="customer-sleeve-questionpanel-empty text-center text-secondary">
                                    Select a question from the left to answer
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </section>

                      {/* Password Modal */}
                      <button
                        type="button"
                        className="btn btn-primary d-none"
                        data-bs-toggle="modal"
                        ref={openPasswordModal}
                        data-bs-target="#passwordModal"
                      >
                      </button>
                      <div
                        className="modal fade"
                        id="passwordModal"
                        tabIndex="-1"
                        aria-labelledby="exampleModalLabel"
                        data-bs-backdrop="static"
                        data-bs-keyboard="true"
                        aria-hidden="true"
                      >
                        <div className="modal-dialog">
                          <div className="modal-content">
                            <div className="modal-header">
                              <h1 className="modal-title fs-5" id="exampleModalLabel">Enter Password</h1>
                            </div>
                            <div className="modal-body">
                              <div className="form-group">
                                <input
                                  type="password"
                                  className="form-control"
                                  value={password}
                                  onChange={(e) => setPassword(e.target.value)}
                                  placeholder="Enter password"
                                />
                                {errors.password && (
                                  <div className="text-danger mt-1 ms-2">{errors.password}</div>
                                )}
                              </div>
                            </div>
                            <div className="modal-footer ">
                              <button
                                type="button"
                                className="btn btn-secondary d-none"
                                ref={clsPasswordModal}
                                data-bs-dismiss="modal"
                              >
                                Close
                              </button>
                              <button
                                type="button"
                                onClick={handlePasswordForm}
                                className="btn btn-primary"
                              >
                                Submit
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="error_Wrapper">
                      <div className="card">
                        <span className="text-success mb-3 error_icon">
                          <i className="bi bi-check-circle-o" aria-hidden="true" />
                        </span>
                        <h2 className=" mb-2"><b>Completed</b></h2>
                        {envelopeData.finish_message || "Your Questionnaire has been submitted successfully!"}
                      </div>
                    </div>
                  )}
                </>
              )}

              {error && (
                <div className="error_Wrapper">
                  <div className="card">
                    <span className="error_icon mb-3">
                      <i className="bi bi-exclamation-triangle-fill fs-1 text-danger" aria-hidden="true" />
                    </span>
                    <h2 className=" mb-2"><b>ERROR</b></h2>
                    {error}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Preview Modal */}
          {showPreviewModal && (
            <div className="preview-modal-container">
              <div className="preview-modal-backdrop">
                <div className="preview-modal-dialog">
                  <div className="preview-modal-content">
                    <div className="preview-modal-header">
                      <h5 className="preview-modal-title">
                        Preview Answers
                      </h5>
                      <button
                        type="button"
                        className="preview-modal-close"
                        onClick={() => setShowPreviewModal(false)}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    </div>
                    <div className="preview-modal-body">
                      <div className="preview-modal-row">
                        {/* Sidebar */}
                        <div className="preview-sidebar">
                          {envelopeData?.forms?.map((form, fidx) => (
                            <div key={form.form_id} className="preview-form-item">
                              <div className="preview-form-title">
                                {form.form_name}
                              </div>
                              <ul className="preview-group-list">
                                {form.groups.map((group, gidx) => (
                                  <li
                                    key={group.group_id}
                                    className={`preview-group-item ${selectedPreviewFormIndex === fidx &&
                                      selectedPreviewGroupIndex === gidx ? 'active' : ''
                                      }`}
                                    onClick={() => {
                                      setSelectedPreviewFormIndex(fidx);
                                      setSelectedPreviewGroupIndex(gidx);
                                    }}
                                  >
                                    <span className="preview-step-indicator">
                                      {gidx + 1}
                                    </span>
                                    <span className="preview-group-name">
                                      {group.group_name}
                                    </span>
                                    <span className="preview-group-stats">
                                      {(() => {
                                        const total = group.questions.length;
                                        const attempted = group.questions.filter(q => {
                                          const ans = questionAnswers[q.question_id];
                                          if (q.question_type === 'checkbox') return Array.isArray(ans) && ans.length > 0;
                                          return !!ans;
                                        }).length;
                                        return `(${attempted}/${total})`;
                                      })()}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>

                        {/* Preview Content */}
                        <div className="preview-content-panel">
                          {envelopeData?.forms?.[selectedPreviewFormIndex]?.groups?.[selectedPreviewGroupIndex] ? (
                            <div className="preview-answer-container">
                              <div className="preview-group-info">
                                Group: <span>
                                  {envelopeData.forms[selectedPreviewFormIndex].groups[selectedPreviewGroupIndex].group_name}
                                </span>
                              </div>
                              {envelopeData.forms[selectedPreviewFormIndex].groups[selectedPreviewGroupIndex].questions.map((q, qidx) => (
                                <div key={q.question_id} className="preview-question-item">
                                  <div className="preview-question-text">
                                    <i className={`bi ${getQuestionIcon(q.question_type)} preview-question-icon`} />
                                    {qidx + 1}. {q.question_text}
                                  </div>
                                  <div className="preview-answer-content">
                                    {renderPreviewAnswer(q)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="preview-empty-state">
                              Select a group to preview answers
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="preview-modal-footer gap-2">
                      <button
                        type="button"
                        className="preview-cancel-btn"
                        onClick={() => setShowPreviewModal(false)}
                      >
                        <i className="bi bi-x-circle preview-btn-icon" />Close
                      </button>
                      <button
                        type="button"
                        className="preview-submit-btn"
                        onClick={handleSubmitAnswers}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="preview-spinner"></span>Submitting...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-send-check preview-btn-icon" />Submit
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CustomerSleeveLayout>
      </section>
    </>
  );
};

export default CustomerSleeve;